import React, { FC, useEffect, useState } from 'react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { observer } from 'mobx-react-lite';
import clone from 'lodash/clone';
import { useStore } from 'hooks';
import { checkSelect } from '../../../../../../utils';
import { IObject } from 'types';
import { FiasSearchProps } from '../../../../../../types';
import { InlineSpinner, TooltipInner } from 'components';
import {
  ScrollWrapper,
  Ul,
  Wrapper,
  Text,
  CheckIcon,
  SpinnerWrapper,
  SearchWrapper,
  SearchInput,
  SearchInfoIcon,
  StyledReactTooltip,
} from './second-menu-options.styled';

type SecondMenuOptionsProps = Pick<FiasSearchProps, 'flat' | 'disabledHouses'>;

export const SecondMenuOptions: FC<SecondMenuOptionsProps> = observer(
  ({ flat, disabledHouses }) => {
    const { fias } = useStore();
    const [currentFlats, setCurrentFlats] = useState<IObject>();

    const options = flat
      ? [
          ...Array.from(
            { length: fias.flatLivingCount },
            (_, index) => `${index + 1}`
          ),
          ...Array.from(
            { length: fias.flatNotLivingCount },
            (_, index) => `${index + 1}Н`
          ),
          ...Array.from(
            { length: fias.flatParkingCount },
            (_, index) => `${index + 1}П`
          ),
        ]
      : fias.lastSelectedHouse?.houses;

    // Во избежание пустого/некорректного currentFlat в случае, если выбор дома был отменен и затем вновь выбран
    useEffect(() => {
      checkCurrentFlats();
    }, [fias.currentSelectedHouses]);

    // Очистка инпута в квартирах при смене выбранного дома
    useEffect(() => {
      fias.setCurrentValueFlatSearch('');
      checkCurrentFlats();
    }, [fias.lastSelectedHouse]);

    // Запись flatsRange
    useEffect(() => {
      const params = getParamsWithoutLastSelected();
      const newParam = checkCurrentFlatsId();
      newParam.flatsRange = fias.currentValueFlatSearch;

      if (!newParam.flats) {
        newParam.flats = [];
      }

      setCurrentFlats(newParam);
      fias.setCurrentSelectedFlats([...params, newParam]);
    }, [fias.currentValueFlatSearch]);

    const checkCurrentFlats = () => {
      let params = clone(fias.currentSelectedFlats);
      params = params.filter(
        (item: any) => item._id === fias.lastSelectedHouse._id
      );

      if (!!params.length) {
        setCurrentFlats(params[0]);
      } else {
        setCurrentFlats(fias.lastSelectedHouse);
      }
    };

    const checkCurrentFlatsId = () => {
      if (currentFlats?._id !== fias.lastSelectedHouse._id) {
        return { ...fias.lastSelectedHouse };
      }

      return { ...currentFlats };
    };

    const getParamsWithoutLastSelected = () => {
      const params = clone(fias.currentSelectedFlats);

      return params.filter(
        (item: any) => item._id !== fias.lastSelectedHouse._id
      );
    };

    const checkCurrentHouseDisabled = (currentItem: any) => {
      return currentItem?._id
        ? disabledHouses.find((house) => house.id === currentItem._id)
        : undefined;
    };

    const handleChangeSelectedHouse = (currentItem: any) => {
      let params = clone(fias.currentSelectedHouses);

      // Нижний уровень должен знать своего родителя
      currentItem.parent = fias.lastSelectedHouse.AOGUID;

      if (checkCurrentHouseDisabled(currentItem)) {
        return;
      }

      if (checkSelect(currentItem, params)) {
        params = params.filter((item) => item._id !== currentItem._id);
      } else {
        params.push(currentItem);
      }

      fias.setCurrentSelectedHouses(params);
    };

    const handleChangeSelectedFlat = (currentItem: any) => {
      const params = getParamsWithoutLastSelected();
      const newParam = checkCurrentFlatsId();

      if (!newParam.flats) {
        newParam.flats = [];
      }

      if (newParam.flats.includes(currentItem)) {
        newParam.flats = newParam.flats.filter(
          (item: any) => item !== currentItem
        );
      } else {
        newParam.flats.push(currentItem);
      }

      setCurrentFlats(newParam);
      fias.setCurrentSelectedFlats([...params, newParam]);
    };

    const checkSelectFlat = (item: any) => {
      let params = clone(fias.currentSelectedFlats);
      params = params.filter(
        (item: any) => item._id === fias.lastSelectedHouse._id
      );

      return !!(
        !!params?.length &&
        !!params[0]?.flats.length &&
        params[0]?.flats.includes(item)
      );
    };

    return (
      <ScrollWrapper>
        {fias.statuses.flatCount.pending ? (
          <SpinnerWrapper>
            <InlineSpinner style={{ margin: 'auto auto' }} />
          </SpinnerWrapper>
        ) : (
          <>
            {flat && (
              <SearchWrapper>
                <SearchInput
                  value={fias.currentValueFlatSearch}
                  onChange={(e) =>
                    fias.setCurrentValueFlatSearch(e.target.value.toUpperCase())
                  }
                  autoCorrect={'off'}
                  spellCheck={'false'}
                  placeholder={'Выбрать квартиры'}
                />
                <SearchInfoIcon
                  data-tooltip-id={'flat_range'}
                  data-tooltip-variant={'info'}
                  data-tooltip-position-strategy={'fixed'}
                />
                <StyledReactTooltip id={'flat_range'} place={'top'}>
                  <div
                    style={{
                      fontSize: '14px',
                      lineHeight: '24px',
                      zIndex: '9999',
                    }}
                  >
                    Для определения диапазона квартир <br /> используйте дефис
                    «-» (например, 1-100) <br /> или запятую «,» (например,
                    1,2,3,10,100).
                    <br />
                    Для определения типов помещений используйте <br /> «Н» для
                    нежилых и «П» для паркингов.
                    <br />
                    Например: «1П-100П, 10Н, 20Н» <br /> вернет 100 паркингов и
                    2 нежилых помещения.
                  </div>
                </StyledReactTooltip>
              </SearchWrapper>
            )}
            <Ul>
              {options.map((item: any, index: number) => (
                <li key={index}>
                  <Wrapper
                    $isDisabled={!!checkCurrentHouseDisabled(item)}
                    data-tooltip-id={item?._id}
                    data-tooltip-variant={'info'}
                  >
                    <Text
                      onClick={() =>
                        flat
                          ? handleChangeSelectedFlat(item)
                          : handleChangeSelectedHouse(item)
                      }
                    >
                      {flat ? item : item.short_address}
                    </Text>
                    {flat
                      ? checkSelectFlat(item) && (
                          <CheckIcon
                            onClick={() => handleChangeSelectedFlat(item)}
                          />
                        )
                      : checkSelect(item, fias.currentSelectedHouses) && (
                          <CheckIcon
                            onClick={() => handleChangeSelectedHouse(item)}
                          />
                        )}
                  </Wrapper>
                  {!!checkCurrentHouseDisabled(item) && (
                    <ReactTooltip id={item?._id} variant={'info'}>
                      <TooltipInner>
                        {checkCurrentHouseDisabled(item)!.reason}
                      </TooltipInner>
                    </ReactTooltip>
                  )}
                </li>
              ))}
            </Ul>
          </>
        )}
      </ScrollWrapper>
    );
  }
);
